import { httpClient } from './httpClient';
import type {
  FetchSupportMessagesParams,
  FetchSupportMessagesResponse,
  SupportMessageDTO,
  SupportOutgoingVoice,
  SupportSubmitPayload,
  SupportThreadDTO,
} from '../types/support';

/**
 * REST-клиент чата поддержки.
 * См. SUPPORT_CHAT_FRONTEND.md, SUPPORT_CHAT_VOICE_FRONTEND.md.
 */

interface ThreadResponse {
  success: boolean;
  thread: SupportThreadDTO | null;
}

interface MessagesResponse {
  success: boolean;
  messages: SupportMessageDTO[];
  hasMore: boolean;
}

interface SendMessageResponse {
  success: boolean;
  message: SupportMessageDTO;
}

/** GET /support/thread — текущий активный тред или null. */
export async function fetchSupportThread(): Promise<SupportThreadDTO | null> {
  const data = await httpClient.request<ThreadResponse>('/support/thread', {
    method: 'GET',
  });
  return data.thread ?? null;
}

/** GET /support/messages?limit&before — история (старые → новые). */
export async function fetchSupportMessages(
  params: FetchSupportMessagesParams = {},
): Promise<FetchSupportMessagesResponse> {
  const search = new URLSearchParams();
  if (params.limit) search.set('limit', String(params.limit));
  if (params.before) search.set('before', params.before);
  const query = search.toString();
  const url = `/support/messages${query ? `?${query}` : ''}`;

  const data = await httpClient.request<MessagesResponse>(url, { method: 'GET' });
  return { messages: data.messages, hasMore: data.hasMore };
}

/**
 * POST /support/messages — текст, вложения (`attachments`) и/или голосовое (`voice`).
 * Голосовую запись нельзя класть в `attachments` — только в `voice`.
 */
export async function sendSupportMessage(payload: SupportSubmitPayload): Promise<SupportMessageDTO> {
  const { text, attachments, voice } = payload;
  const fd = new FormData();

  if (text.trim()) fd.append('text', text.trim());

  if (voice) {
    fd.append('voice', voice.file, voice.file.name);
    if (voice.durationSec != null) {
      fd.append('voiceDurationSec', String(Math.round(voice.durationSec)));
    }
  }

  for (const { file } of attachments) {
    fd.append('attachments', file, file.name);
  }

  const data = await httpClient.request<SendMessageResponse>('/support/messages', {
    method: 'POST',
    body: fd,
  });

  if (voice && process.env.NODE_ENV === 'development') {
    const voiceAtt = data.message.attachments?.find((a) => a.kind === 'voice');
    if (!voiceAtt) {
      console.warn('[support] Ожидали kind=voice в ответе', data.message);
    }
  }

  return data.message;
}

/** POST /support/read — сбросить unreadForUser. */
export async function markSupportRead(): Promise<void> {
  await httpClient.request('/support/read', {
    method: 'POST',
    responseType: 'void',
  });
}

/**
 * Загружает вложение как Blob (без object URL).
 * Нужен, потому что endpoint защищён `Authorization: Bearer …`.
 */
export async function fetchSupportAttachmentBlob(attachmentId: string): Promise<Blob> {
  const response = await httpClient.fetch(`/support/attachments/${attachmentId}`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`Не удалось загрузить вложение (${response.status})`);
  }
  return response.blob();
}

/**
 * Загружает вложение как Blob и возвращает object URL.
 * @deprecated Предпочитайте resolveSupportAttachmentBlob + URL.createObjectURL в UI.
 */
export async function fetchSupportAttachmentBlobUrl(attachmentId: string): Promise<string> {
  const blob = await fetchSupportAttachmentBlob(attachmentId);
  return URL.createObjectURL(blob);
}
